// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * IncomingCallAlert Component
 *
 * Header notification banner for incoming calls.
 * Shows patient info with accept/reject buttons.
 */

import { useEffect, useState } from 'react';
import {
  Box,
  Group,
  Text,
  Avatar,
  Button,
  Paper,
  Portal,
} from '@mantine/core';
import { IconPhone, IconPhoneOff, IconVideo } from '@tabler/icons-react';
import { getDisplayString } from '@medplum/core';
import { useTranslation } from 'react-i18next';
import { useCareComm } from '../../contexts/CareCommContext';
import classes from './CareCommPanel.module.css';

export function IncomingCallAlert() {
  const { t } = useTranslation();
  const { incomingCall, acceptCall, rejectCall } = useCareComm();
  const [ringTime, setRingTime] = useState(0);

  // Ring timer
  useEffect(() => {
    if (!incomingCall) {
      setRingTime(0);
      return;
    }

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - incomingCall.ringStartTime) / 1000);
      setRingTime(elapsed);

      // Auto-reject after 60 seconds
      if (elapsed >= 60) {
        rejectCall();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [incomingCall, rejectCall]);

  // Don't render if no incoming call
  if (!incomingCall) {
    return null;
  }

  const patientName = getDisplayString(incomingCall.patient);
  const isVideoCall = incomingCall.type === 'video-call';

  return (
    <Portal>
      <Box className={classes.incomingCallContainer}>
        <Paper
          shadow="xl"
          radius="lg"
          p="md"
          className={classes.incomingCallBanner}
        >
          <Group justify="space-between" wrap="nowrap" gap="lg">
            {/* Caller info */}
            <Group gap="md" wrap="nowrap">
              <Box className={classes.callerAvatarContainer}>
                <Avatar size={48} radius={48} color="blue" className={classes.callerAvatar}>
                  {patientName.charAt(0).toUpperCase()}
                </Avatar>
                {/* Ringing animation */}
                <Box className={classes.ringAnimation} />
              </Box>

              <Box>
                <Group gap={4} align="center">
                  {isVideoCall ? (
                    <IconVideo size={16} color="var(--mantine-color-blue-6)" />
                  ) : (
                    <IconPhone size={16} color="var(--mantine-color-green-6)" />
                  )}
                  <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                    {isVideoCall
                      ? t('carecomm.incomingVideoCall', 'Incoming Video Call')
                      : t('carecomm.incomingVoiceCall', 'Incoming Voice Call')}
                  </Text>
                </Group>
                <Text fw={600} size="md">
                  {patientName}
                </Text>
                <Text size="xs" c="dimmed">
                  {t('carecomm.ringingFor', 'Ringing for {{seconds}}s', { seconds: ringTime })}
                </Text>
              </Box>
            </Group>

            {/* Action buttons */}
            <Group gap="sm" wrap="nowrap">
              {/* Reject */}
              <Button
                variant="filled"
                color="red"
                leftSection={<IconPhoneOff size={18} />}
                onClick={rejectCall}
                className={classes.declineButton}
              >
                {t('carecomm.decline', 'Decline')}
              </Button>

              {/* Accept */}
              <Button
                variant="filled"
                color="green"
                leftSection={
                  isVideoCall ? (
                    <IconVideo size={18} />
                  ) : (
                    <IconPhone size={18} />
                  )
                }
                onClick={acceptCall}
                className={classes.acceptButton}
              >
                {t('carecomm.accept', 'Accept')}
              </Button>
            </Group>
          </Group>
        </Paper>
      </Box>
    </Portal>
  );
}

export default IncomingCallAlert;
